<!--
결제수단 (예치금/카드자동결제)
------------------------------------
-->
<template>
    <div class="ps-b">

        <h3 class="uk-heading-divider">결제수단 (예치금/카드자동결제)</h3>

        <div class="section-title">등록된 결제수단</div>

        <!-- 예치금 -->
        <div class="pay-card" :class="{ 'is-primary': isPrimary(null) }">
            <div class="pay-card-top">
                <div class="pay-card-info">
                    <div class="pay-card-icon icon-deposit">&#8361;</div>
                    <div>
                        <div class="pay-card-name">예치금결제</div>
                        <div class="pay-card-desc">잔액에서 자동 차감</div>
                    </div>
                </div>
                <div class="pay-card-actions">
                    <span v-if="isPrimary(null)" class="badge badge-primary">&#9733; 주 결제수단</span>
                    <button v-else type="button" class="btn-ps btn-ps-outline" @click="setPrimary(null)">주 결제수단으로 설정</button>
                </div>
            </div>
            <div class="pay-card-bottom">
                <span class="badge badge-free">수수료 없음</span>
            </div>
        </div>

        <!-- 등록된 카드 목록 -->
        <div
            v-for="method in methods"
            :key="method.id"
            class="pay-card"
            :class="{ 'is-primary': isPrimary(method.id) }"
        >
            <div class="pay-card-top">
                <div class="pay-card-info">
                    <div class="pay-card-icon icon-card">{{ cardInitial(method.card_name) }}</div>
                    <div>
                        <div class="pay-card-name">{{ method.card_name }}</div>
                        <div class="pay-card-desc pay-card-digits">**** **** **** {{ method.card_last_digits }}</div>
                    </div>
                </div>
                <div class="pay-card-actions">
                    <span v-if="isPrimary(method.id)" class="badge badge-primary">&#9733; 주 결제수단</span>
                    <button v-else type="button" class="btn-ps btn-ps-outline" @click="setPrimary(method.id)">주 결제수단으로 설정</button>
                    <button type="button" class="btn-ps btn-ps-danger" @click="removeCard(method.id)">삭제</button>
                </div>
            </div>
            <div class="pay-card-bottom">
                <span class="badge badge-fee">수수료 {{ feeRatePercent }}% (VAT별도)</span>
                <span class="badge badge-active">사용가능</span>
            </div>
        </div>

        <!-- 카드 추가 버튼 -->
        <button v-show="!showConsentPanel && !showAddPanel" type="button" class="btn-add" @click="onAddCardClick()">
            <span class="btn-add-icon">+</span>
            새 카드 등록하기
        </button>

        <!-- R-02: 해외카드 미지원 안내 -->
        <p v-show="!showConsentPanel && !showAddPanel" class="notice-sub">※ 해외카드는 지원되지 않습니다</p>

        <!-- 카드자동결제 이용약관 동의 (인라인: 버튼 클릭 후 표시) -->
        <div v-if="hasConsentTerms" v-show="showConsentPanel" class="consent-panel">
            <div v-for="(term, index) in consentTerms" :key="'consent-' + index" class="consent-card">
                <div class="consent-header">
                    <label class="consent-label">
                        <input class="uk-checkbox consent-checkbox" type="checkbox" v-model="consentAgreed">
                        <span class="consent-title">{{ term.title }}에 동의합니다</span>
                        <span class="consent-required">(필수)</span>
                    </label>
                    <a href="#" class="consent-toggle" @click.prevent="consentExpanded = !consentExpanded">
                        {{ consentExpanded ? '접기' : '내용 보기' }}
                    </a>
                </div>
                <div v-show="consentExpanded" class="consent-body">
                    <div class="consent-content">{{ term.content }}</div>
                </div>
                <div class="panel-actions">
                    <button type="button" class="btn-ps btn-ps-ghost" @click="cancelConsent()">취소</button>
                    <button type="button" class="btn-ps btn-ps-primary" @click="proceedWithRegistration()">동의하고 카드 등록</button>
                </div>
            </div>
        </div>

        <!-- 카드 추가 슬라이드 패널 (MOCK 모드 전용) -->
        <div v-if="isMockMode" v-show="showAddPanel" class="slide-panel">
            <div class="slide-panel-header">
                새 카드 등록
                <button type="button" class="slide-panel-close" @click="toggleAddPanel()">&times;</button>
            </div>
            <div class="slide-panel-body">
                <div class="form-group">
                    <label>카드번호 (16자리, 숫자만)</label>
                    <input type="text" v-model="form.card_number" class="form-control"
                           maxlength="16" placeholder="0000000000000000">
                </div>
                <div class="form-group">
                    <label>유효기간 (YYMM)</label>
                    <input type="text" v-model="form.expiry" class="form-control form-control-short"
                           maxlength="4" placeholder="2512">
                </div>
                <div class="form-group">
                    <label>인증정보 (생년월일 6자리 또는 사업자번호 10자리)</label>
                    <input type="text" v-model="form.auth_info" class="form-control"
                           maxlength="10" placeholder="생년월일 6자리 또는 사업자번호 10자리">
                </div>
                <div class="form-group">
                    <label>카드 비밀번호 앞 2자리</label>
                    <input type="password" v-model="form.card_password" class="form-control form-control-narrow"
                           maxlength="2" placeholder="**">
                </div>
                <div class="panel-actions">
                    <button type="button" class="btn-ps btn-ps-ghost" @click="toggleAddPanel()">취소</button>
                    <button type="button" class="btn-ps btn-ps-primary" @click="submitAddCard()">등록하기</button>
                </div>
            </div>
        </div>

        <!-- 스마트로 SDK hidden form (STG/REAL 모드) -->
        <form v-if="!isMockMode && sdkParams" id="smartroForm" ref="smartroForm" method="post" style="display:none;">
            <input type="hidden" name="PayMethod" :value="sdkParams.PayMethod">
            <input type="hidden" name="Mid" :value="sdkParams.Mid">
            <input type="hidden" name="Moid" :value="sdkParams.Moid">
            <input type="hidden" name="MallUserId" :value="sdkParams.MallUserId">
            <input type="hidden" name="ReturnUrl" :value="sdkParams.ReturnUrl">
            <input type="hidden" name="VerifyValue" :value="sdkParams.VerifyValue">
            <input type="hidden" name="SspMallId" :value="sdkParams.SspMallId">
            <input type="hidden" name="EdiDate" :value="sdkParams.EdiDate">
            <input v-if="sdkParams.GoodsName" type="hidden" name="GoodsName" :value="sdkParams.GoodsName">
            <input v-if="sdkParams.BuyerName" type="hidden" name="BuyerName" :value="sdkParams.BuyerName">
            <input v-if="sdkParams.BuyerTel" type="hidden" name="BuyerTel" :value="sdkParams.BuyerTel">
        </form>

        <!-- 결제수단별 정책 안내 -->
        <div class="fee-info">
            <div class="fee-info-title">
                <i class="material-icons">info_outline</i>
                결제수단별 정책 안내
            </div>
            <table class="policy-table">
                <thead>
                    <tr>
                        <th>결제수단</th>
                        <th>수수료</th>
                        <th>세금계산서</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>예치금</td>
                        <td><span class="text-free">없음</span></td>
                        <td>발행</td>
                    </tr>
                    <tr>
                        <td>신용카드</td>
                        <td><span class="text-fee">{{ feeRatePercent }}% (VAT별도)</span></td>
                        <td>미발행</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 결제 시점 안내 -->
        <div class="billing-schedule-info">
            <div class="billing-schedule-icon">
                <i class="material-icons">schedule</i>
            </div>
            <div class="billing-schedule-text">
                <p class="billing-schedule-main">전일 출고운임은 익영업일 오전에 자동 결제됩니다.</p>
                <p class="billing-schedule-sub">주말·공휴일 발생 운임은 다음 영업일에 합산 결제됩니다.</p>
            </div>
        </div>

    </div>
</template>

<script>
    export default {
        props: {
            csrfToken: { type: String, required: true },
            methods: { type: Array, default: function () { return []; } },
            primaryCardId: { default: null },
            sdkMode: { type: String, default: 'MOCK' },
            sdkParams: { type: Object, default: null },
            cardFeeRate: { type: Number, default: 0.035 },
            arrearsSummary: { type: Object, default: function () { return { cnt: 0, total_amount: 0 }; } },
            consentTerms: { type: Array, default: function () { return []; } },
        },
        data: function () {
            return {
                showAddPanel: false,
                showConsentPanel: false,
                sdkLoaded: false,
                consentAgreed: false,
                consentExpanded: false,
                form: {
                    card_number: '',
                    expiry: '',
                    auth_info: '',
                    card_password: '',
                },
            };
        },
        computed: {
            isMockMode: function () {
                return this.sdkMode === 'MOCK';
            },
            feeRatePercent: function () {
                return parseFloat((this.cardFeeRate * 100).toFixed(2));
            },
            hasConsentTerms: function () {
                return this.consentTerms.length > 0;
            },
        },
        mounted: function () {
            if (!this.isMockMode && this.sdkParams) {
                this.loadSmartroSdk();
            }
        },
        methods: {
            /**
             * 주 결제수단 여부 판별
             */
            isPrimary: function (id) {
                if (id === null) {
                    return this.primaryCardId === null;
                }
                return this.primaryCardId === id;
            },

            /**
             * 성공 알림 후 페이지 새로고침
             */
            notifyAndRefresh: function (message) {
                UIkit.modal.alert(message).then(function () {
                    $.app.refresh();
                });
            },

            /**
             * 주 결제수단 변경
             */
            setPrimary: function (id) {
                var self = this;
                $.app.ajax.module.default({
                    type: 'post',
                    url: '/my/payment/setting/set-primary',
                    data: {
                        '_token': this.csrfToken,
                        'billing_payment_method_id': id,
                    },
                    success: function () {
                        self.notifyAndRefresh('주 결제수단이 변경되었습니다.');
                    },
                });
            },

            /**
             * 카드 삭제
             */
            removeCard: function (id) {
                var self = this;
                UIkit.modal.confirm('이 카드를 삭제하시겠습니까?').then(function () {
                    $.app.ajax.module.default({
                        type: 'post',
                        url: '/my/payment/setting/' + id + '/remove',
                        data: {
                            '_token': self.csrfToken,
                        },
                        success: function () {
                            self.notifyAndRefresh('카드가 삭제되었습니다.');
                        },
                    });
                }, function () {
                    // 취소
                });
            },

            /**
             * 카드 등록 성공 후 처리 (R-19: 안내 + R-03/R-23: 미수 안내)
             */
            onCardRegistered: function () {
                var self = this;

                // R-03/R-23: 미수 건이 있으면 등록 안내 + 미수 안내를 한 번에
                if (self.arrearsSummary && self.arrearsSummary.cnt > 0) {
                    UIkit.modal.confirm(
                        '카드가 등록되었습니다.\n다음 과금부터 카드 자동결제가 적용됩니다.\n\n' +
                        '미결제 ' + self.arrearsSummary.cnt + '건이 있습니다.\n' +
                        '과금내역에서 재결제하시겠습니까?'
                    ).then(function () {
                        window.location.href = '/my/payment/billings?status=9';
                    }, function () {
                        $.app.refresh();
                    });
                } else {
                    UIkit.modal.alert(
                        '카드가 등록되었습니다.\n다음 과금부터 카드 자동결제가 적용됩니다.'
                    ).then(function () {
                        $.app.refresh();
                    });
                }
            },

            /**
             * 카드 추가 버튼 클릭: 약관이 있으면 동의 패널 표시, 없으면 바로 등록 진행
             */
            onAddCardClick: function () {
                if (this.hasConsentTerms) {
                    this.showConsentPanel = true;
                    return;
                }
                this.startRegistration();
            },

            /**
             * 약관동의 패널 취소
             */
            cancelConsent: function () {
                this.showConsentPanel = false;
                this.consentAgreed = false;
                this.consentExpanded = false;
            },

            /**
             * 약관동의 후 카드 등록 진행
             */
            proceedWithRegistration: function () {
                if (!this.consentAgreed) {
                    UIkit.modal.alert('카드자동결제 이용약관에 동의해 주세요.');
                    return;
                }
                this.startRegistration();
            },

            /**
             * 실제 카드 등록 흐름 시작 (MOCK: 폼 표시, SDK: 스마트로 호출)
             */
            startRegistration: function () {
                if (this.isMockMode) {
                    this.showConsentPanel = false;
                    this.showAddPanel = true;
                } else {
                    this.openSmartroSdk();
                }
            },

            /**
             * 카드 등록 폼 제출 (MOCK 모드)
             */
            submitAddCard: function () {
                var self = this;

                if (!this.form.card_number || !this.form.expiry || !this.form.auth_info || !this.form.card_password) {
                    UIkit.modal.alert('모든 항목을 입력해 주세요.');
                    return;
                }

                // R-01: 수수료 안내 confirm
                UIkit.modal.confirm(
                    '카드결제 수수료 ' + self.feeRatePercent + '%(VAT별도)가 부과됩니다.\n등록하시겠습니까?'
                ).then(function () {
                    var data = {
                        '_token': self.csrfToken,
                        'card_number': self.form.card_number,
                        'expiry': self.form.expiry,
                        'auth_info': self.form.auth_info,
                        'card_password': self.form.card_password,
                    };
                    if (self.hasConsentTerms) {
                        data['consent_version_id'] = self.consentTerms[0].consentVersionId;
                    }
                    $.app.ajax.module.default({
                        type: 'post',
                        url: '/my/payment/setting/add-card',
                        data: data,
                        success: function (response) {
                            if (response.success === false) {
                                UIkit.modal.alert(response.msg || '카드 등록에 실패했습니다.');
                                return;
                            }
                            self.onCardRegistered();
                        },
                    });
                }, function () {
                    // 취소
                });
            },

            /**
             * 스마트로 SDK JS 동적 로드
             */
            loadSmartroSdk: function () {
                var self = this;
                var baseUrl = (this.sdkMode === 'REAL')
                    ? 'https://pay.smartropay.co.kr'
                    : 'https://tpay.smartropay.co.kr';
                var scriptUrl = baseUrl + '/asset/js/SmartroPAY-1.0.min.js';

                var script = document.createElement('script');
                script.src = scriptUrl;
                script.onload = function () {
                    // SDK 초기화 (payment() 호출 전 필수)
                    window.smartropay.init({
                        mode: self.sdkMode,
                        actionUri: '/ssb/interface.do'
                    });
                    self.sdkLoaded = true;
                };
                script.onerror = function () {
                    console.error('스마트로 SDK 로드 실패:', scriptUrl);
                };
                document.head.appendChild(script);
            },

            /**
             * 스마트로 SDK 호출 (STG/REAL 모드)
             * PC: 레이어 팝업(iframe) → Callback 함수로 결과 수신
             * 모바일: 전체 화면 이동 → ReturnUrl POST 리다이렉트
             */
            openSmartroSdk: function () {
                var self = this;
                if (!this.sdkLoaded || typeof window.smartropay === 'undefined') {
                    UIkit.modal.alert('결제 모듈을 로드하는 중입니다. 잠시 후 다시 시도해 주세요.');
                    return;
                }

                // R-01: 수수료 안내 confirm
                UIkit.modal.confirm(
                    '카드결제 수수료 ' + self.feeRatePercent + '%(VAT별도)가 부과됩니다.\n등록하시겠습니까?'
                ).then(function () {
                    // PC 환경: SDK가 Callback 함수를 호출하여 결과를 전달한다
                    window.__smartroIssueCallback = function (result) {
                        // ResultCode 3001이 아니면 실패/취소
                        if (result.ResultCode !== '3001') {
                            UIkit.notification({ message: result.ResultMsg || '카드 등록이 취소되었습니다.', status: 'warning', timeout: 3000 });
                            return;
                        }
                        // 콜백 데이터를 서버로 AJAX 전달
                        var ajaxData = $.extend({ '_token': self.csrfToken }, result);
                        if (self.hasConsentTerms) {
                            ajaxData['consent_version_id'] = self.consentTerms[0].consentVersionId;
                        }
                        $.app.ajax.module.default({
                            type: 'post',
                            url: '/my/payment/setting/process-issue-ajax',
                            data: ajaxData,
                            success: function (response) {
                                if (response.success === false) {
                                    UIkit.modal.alert(response.msg || '카드 등록에 실패했습니다.');
                                    return;
                                }
                                self.onCardRegistered();
                            },
                        });
                    };

                    window.smartropay.payment({ FormId: 'smartroForm', Callback: window.__smartroIssueCallback });
                }, function () {
                    // 취소
                });
            },

            /**
             * 카드 추가 패널 토글
             */
            toggleAddPanel: function () {
                this.showAddPanel = !this.showAddPanel;
            },

            /**
             * 카드명 첫 글자 추출
             */
            cardInitial: function (name) {
                return name ? name.charAt(0) : '';
            },
        },
    }
</script>

<style>
    /* ── 레이아웃 ── */
    .ps-b { max-width: 900px; margin: 30px auto; padding: 0 20px; }

    /* ── 섹션 타이틀 ── */
    .ps-b .section-title {
        font-size: 1rem; font-weight: 600; color: #666;
        text-transform: uppercase; letter-spacing: 1px;
        margin-bottom: 16px; margin-top: 30px;
    }

    /* ── 결제수단 카드 ── */
    .ps-b .pay-card {
        background: #fff; border-radius: 12px; padding: 20px 24px;
        margin-bottom: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        transition: box-shadow 0.2s ease;
        position: relative; overflow: hidden; border: 2px solid transparent;
    }
    .ps-b .pay-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.09); }
    .ps-b .pay-card.is-primary { border-color: #1e87f0; }
    .ps-b .pay-card.is-primary::before {
        content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
        background: linear-gradient(90deg, #344766, #1e87f0);
    }
    .ps-b .pay-card-top {
        display: flex; align-items: center; justify-content: space-between;
    }
    .ps-b .pay-card-info { display: flex; align-items: center; gap: 14px; }
    .ps-b .pay-card-icon {
        width: 44px; height: 44px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        font-size: 1.2rem; font-weight: 700; flex-shrink: 0;
    }
    .ps-b .icon-deposit { background: linear-gradient(135deg, #32d296, #6edcb8); color: #fff; }
    .ps-b .icon-card { background: linear-gradient(135deg, #344766, #4a6a8a); color: #fff; }
    .ps-b .pay-card-name { font-size: 1.05rem; font-weight: 600; }
    .ps-b .pay-card-desc { color: #888; font-size: 1rem; margin-top: 2px; }
    .ps-b .pay-card-digits { font-family: 'Courier New', monospace; letter-spacing: 2px; }
    .ps-b .pay-card-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
    .ps-b .pay-card-bottom { display: flex; align-items: center; gap: 10px; margin-top: 10px; }

    /* ── 뱃지 ── */
    .ps-b .badge {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 3px 10px; border-radius: 6px;
        font-size: 1rem; font-weight: 500;
    }
    .ps-b .badge-primary { background: #e8f4fd; color: #1e87f0; }
    .ps-b .badge-fee { background: #fef4e6; color: #d48806; }
    .ps-b .badge-free { background: #e6f9f1; color: #28a879; }
    .ps-b .badge-active { background: #e6f9f1; color: #28a879; }

    /* ── 버튼 공통 ── */
    .ps-b .btn-ps {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 7px 16px; font-size: 1rem; border: none; border-radius: 8px;
        cursor: pointer; font-weight: 500; transition: all 0.15s ease;
    }
    .ps-b .btn-ps-primary { background: #344766; color: #fff; }
    .ps-b .btn-ps-primary:hover { background: #2a3a55; }
    .ps-b .btn-ps-outline { background: #fff; color: #1e87f0; border: 1.5px solid #1e87f0; }
    .ps-b .btn-ps-outline:hover { background: #e8f4fd; }
    .ps-b .btn-ps-ghost { background: transparent; color: #888; }
    .ps-b .btn-ps-ghost:hover { background: #f5f5f5; color: #666; }
    .ps-b .btn-ps-danger { background: transparent; color: #bbb; }
    .ps-b .btn-ps-danger:hover { color: #f0506e; background: #fef0f3; }

    /* ── 카드 추가 버튼 ── */
    .ps-b .btn-add {
        background: #fff; border: 2px dashed #d0d0d0; border-radius: 12px;
        padding: 22px; width: 100%; font-size: 1rem; color: #999;
        cursor: pointer; transition: all 0.2s ease;
        display: flex; align-items: center; justify-content: center; gap: 8px;
        margin-top: 4px;
    }
    .ps-b .btn-add:hover { border-color: #1e87f0; color: #1e87f0; background: #f8fbff; }
    .ps-b .btn-add-icon { font-size: 1.3rem; }

    /* ── 안내 문구 ── */
    .ps-b .notice-sub { text-align: center; color: #aaa; margin-top: 8px; font-size: 1rem; }

    /* ── 약관동의 패널 ── */
    .ps-b .consent-panel { margin-top: 16px; }
    .ps-b .consent-card {
        background: #fff; border-radius: 12px; padding: 20px 24px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.06); border: 1.5px solid #b8d4f0;
    }
    .ps-b .consent-header {
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
    }
    .ps-b .consent-label { display: flex; align-items: center; cursor: pointer; flex: 1; min-width: 0; }
    .ps-b .consent-checkbox { width: 20px; height: 20px; min-width: 20px; }
    .ps-b .consent-title { margin-left: 10px; font-weight: 500; font-size: 1rem; }
    .ps-b .consent-required { color: #f0506e; margin-left: 4px; font-size: 1rem; flex-shrink: 0; }
    .ps-b .consent-toggle { white-space: nowrap; color: #1e87f0; font-size: 1rem; flex-shrink: 0; }
    .ps-b .consent-toggle:hover { color: #0f6ecd; }
    .ps-b .consent-body { border-top: 1px solid #eee; margin-top: 16px; padding-top: 16px; }
    .ps-b .consent-content {
        max-height: 300px; overflow-y: auto; padding: 14px;
        border: 1px solid #e5e5e5; border-radius: 8px; background: #fafafa;
        white-space: pre-wrap; font-size: 1rem; line-height: 1.7;
    }

    /* ── 패널 공통 액션 ── */
    .ps-b .panel-actions { margin-top: 18px; display: flex; gap: 10px; justify-content: flex-end; }

    /* ── MOCK 슬라이드 패널 ── */
    .ps-b .slide-panel {
        background: #fff; border-radius: 12px; margin-top: 16px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.06); overflow: hidden;
    }
    .ps-b .slide-panel-header {
        background: #f5f8fc; padding: 16px 24px; font-weight: 600; font-size: 1.05rem;
        display: flex; justify-content: space-between; align-items: center;
        border-bottom: 1px solid #eee;
    }
    .ps-b .slide-panel-body { padding: 24px; }
    .ps-b .slide-panel-close {
        background: none; border: none; font-size: 1.3rem; cursor: pointer; color: #999;
        width: 32px; height: 32px; border-radius: 8px;
        display: flex; align-items: center; justify-content: center;
    }
    .ps-b .slide-panel-close:hover { background: #eee; }

    /* ── 폼 ── */
    .ps-b .form-group { margin-bottom: 16px; }
    .ps-b .form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 1rem; color: #444; }
    .ps-b .form-control {
        width: 100%; padding: 10px 14px;
        border: 1.5px solid #e0e0e0; border-radius: 8px;
        font-size: 1rem; transition: border-color 0.15s; background: #fafafa;
    }
    .ps-b .form-control:focus { outline: none; border-color: #1e87f0; background: #fff; box-shadow: 0 0 0 3px rgba(30,135,240,0.08); }
    .ps-b .form-control-short { max-width: 200px; }
    .ps-b .form-control-narrow { max-width: 120px; }

    /* ── 수수료 안내 ── */
    .ps-b .fee-info {
        background: #fff; border-radius: 12px; padding: 18px 24px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.06); margin-top: 24px;
    }
    .ps-b .fee-info-title { font-weight: 600; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
    .ps-b .text-free { color: #28a879; font-weight: 600; }
    .ps-b .text-fee { color: #d48806; font-weight: 600; }
    .ps-b .policy-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
    .ps-b .policy-table th {
        background: #f5f8fc; padding: 10px 14px; text-align: left;
        border-bottom: 2px solid #e0e4ea; font-weight: 600; color: #555; font-size: 0.9rem;
    }
    .ps-b .policy-table td {
        padding: 10px 14px; border-bottom: 1px solid #f0f0f0;
    }

    /* ── 결제 시점 안내 ── */
    .ps-b .billing-schedule-info {
        display: flex; align-items: flex-start; gap: 12px;
        background: #f5f8fc; border-radius: 12px; padding: 16px 20px;
        margin-top: 16px; border: 1px solid #e4ecf4;
    }
    .ps-b .billing-schedule-icon { color: #7a9bc5; flex-shrink: 0; line-height: 1; padding-top: 1px; }
    .ps-b .billing-schedule-icon .material-icons { font-size: 1.25rem; }
    .ps-b .billing-schedule-text { flex: 1; }
    .ps-b .billing-schedule-main { font-size: 1rem; color: #444; margin: 0; line-height: 1.5; }
    .ps-b .billing-schedule-sub { font-size: 1rem; color: #888; margin: 4px 0 0; line-height: 1.5; }

    /* ── 모바일 대응 ── */
    @media (max-width: 640px) {
        .ps-b { padding: 0 14px; }
        .ps-b .pay-card { padding: 16px; }
        .ps-b .pay-card-top { flex-direction: column; align-items: flex-start; gap: 12px; }
        .ps-b .pay-card-actions { width: 100%; justify-content: flex-start; }
        .ps-b .consent-header { flex-direction: column; align-items: flex-start; gap: 10px; }
        .ps-b .consent-card { padding: 16px; }
        .ps-b .slide-panel-body { padding: 16px; }
        .ps-b .fee-info { padding: 14px 16px; }
        .ps-b .billing-schedule-info { padding: 14px 16px; }
    }
</style>
